import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  useToast,
  useDisclosure,
  Spinner,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import CourseModal from './CourseModal';
import SearchControls from './components/SearchControls';
import PaginationControls from './components/PaginationControls';
import api from '../../../../utils/api';
import analytics from '../../../../segmentTracking';

const CoursesTab = ({ refreshTrigger }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCourse, setCurrentCourse] = useState(null);
  const [deleteCourse, setDeleteCourse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCourses, setTotalCourses] = useState(0);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  const toast = useToast();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const cancelRef = React.useRef();

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get('/dashboard/courses', {
        params: {
          institution: currentInstitution?._id,
          skip: skip,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });

      setCourses(response.data.courses || []);
      setTotalCourses(response.data.total || 0);

      analytics.track('settings:academic:courses:viewed', {
        coursesCount: response.data.courses?.length || 0,
        totalCourses: response.data.total || 0,
        currentPage,
        itemsPerPage,
        searchTerm: searchTerm,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || 'Failed to fetch courses',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    currentInstitution,
    currentUser,
    toast,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (currentInstitution?._id) {
      fetchCourses();
    }
  }, [fetchCourses, currentInstitution]);

  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && currentInstitution?._id) {
      fetchCourses();
    }
  }, [refreshTrigger, fetchCourses, currentInstitution]);

  // Handlers for SearchControls and PaginationControls
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Course CRUD operations
  const handleAddCourse = () => {
    setCurrentCourse(null);
    onModalOpen();

    analytics.track('settings:academic:courses:addclicked', {
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  const handleEditCourse = (course) => {
    setCurrentCourse(course);
    onModalOpen();

    analytics.track('settings:academic:courses:editclicked', {
      courseId: course._id,
      courseName: course.name,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  const handleDeleteCourse = (course) => {
    setDeleteCourse(course);
    onDeleteOpen();

    analytics.track('settings:academic:courses:deleteclicked', {
      courseId: course._id,
      courseName: course.name,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  const confirmDeleteCourse = async () => {
    if (!deleteCourse) return;

    try {
      await api.delete(`/dashboard/courses/${deleteCourse._id}`);

      toast({
        title: 'Success',
        description: 'Course deleted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      analytics.track('settings:academic:courses:deleted', {
        courseId: deleteCourse._id,
        courseName: deleteCourse.name,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });

      // Refresh the list
      await fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || 'Failed to delete course',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setDeleteCourse(null);
    }
  };

  const handleCourseSaved = async () => {
    // Refresh the courses list
    await fetchCourses();
  };

  return (
    <Box>
      {/* Search Controls */}
      <SearchControls
        searchPlaceholder="Search courses..."
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onSearchChange={handleSearchChange}
        entityName="courses"
        analyticsPrefix="academic:courses"
        loading={loading}
      >
        <Button
          bg="#EF6506"
          color="white"
          _hover={{ bg: '#D55A05' }}
          onClick={handleAddCourse}
        >
          Add Course
        </Button>
      </SearchControls>

      {/* Courses List */}
      <Box>
        {loading ? (
          <Flex justifyContent="center" alignItems="center" py={8}>
            <Spinner size="lg" color="#EF6506" />
          </Flex>
        ) : courses.length > 0 ? (
          <Flex flexDir="column" gap={2}>
            {courses.map((course) => (
              <Flex
                key={course._id}
                justifyContent="space-between"
                alignItems="center"
                p={4}
                bg="white"
                border="1px solid #E7E7E7"
                borderRadius="8px"
                _hover={{ bg: '#F9F9F9' }}
              >
                <Box flex="1">
                  <Text fontSize="16px" fontWeight="600" color="#222222" mb={2}>
                    {course.name}
                  </Text>
                  <Text fontSize="12px" color="#757575">
                    Created: {new Date(course.createdAt).toLocaleDateString()}
                  </Text>
                </Box>
                <Flex gap={2}>
                  <IconButton
                    aria-label="Edit course"
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => handleEditCourse(course)}
                  />
                  <IconButton
                    aria-label="Delete course"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteCourse(course)}
                  />
                </Flex>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Box textAlign="center" py={8}>
            <Text fontSize="16px" color="#757575">
              {searchTerm
                ? 'No courses found matching your search.'
                : 'No courses found. Add your first course to get started.'}
            </Text>
          </Box>
        )}
      </Box>

      {/* Pagination Controls */}
      <PaginationControls
        currentPage={currentPage}
        totalItems={totalCourses}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        entityName="courses"
        analyticsPrefix="academic:courses"
        loading={loading}
      />

      {/* Add/Edit Course Modal */}
      <CourseModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        course={currentCourse}
        onCourseSaved={handleCourseSaved}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Course
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{deleteCourse?.name}"? This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteCourse} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CoursesTab;
